import { useActionConfirmation } from '../../components/ActionFeedbackProvider';
// React and hooks
import React, { useState, useEffect } from 'react';
// Icons
import { Trash, Plus, ChevronLeft, ChevronRight, Search, FileDown, Pencil } from 'lucide-react';
import { Warehouse } from 'lucide-react';
// Animations
import { motion } from 'framer-motion';
// Router
import { useNavigate } from 'react-router-dom';
// Theme hook
import useTheme from '../../hooks/useTheme';
// API client
import api from '../../lib/api';
// Modal component
import AddExitModal from '../../components/modals/AddExitModal';
// NEW: safe edit modal
import EditExitModal from '../../components/modals/EditExitModal';
// i18n
import { useTranslation } from 'react-i18next';

const ExitSColdStorage1 = () => {
  // States
  const [exits, setExits] = useState([]);
  const [filteredExits, setFilteredExits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  // NEW: Edit modal safe mode states
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExit, setSelectedExit] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirmAction = useActionConfirmation();

  const { theme } = useTheme();

  // Fixed key for this screen
  const locationKey = 'coldstorage1';

  // Base button style (Add / Inventory)
  const baseBtn =
    'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 ' +
    'bg-black text-white hover:bg-white hover:text-black hover:scale-105 ' +
    'dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white';

  // Action button style (edit / export / delete)
  const actionBtn =
    'inline-flex items-center justify-center p-2.5 rounded-md shadow transition duration-200 text-sm ' +
    'bg-black text-white hover:bg-white hover:text-black hover:scale-105 ' +
    'dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white';

  // Helper to get ID safely
  const getId = (obj) => obj?.id || obj?._id;

  // Format date (yyyy-mm-dd)
  const formatDate = (date) => date.toISOString().split('T')[0];

  // Get day name (e.g. Tuesday)
  const getDayName = (date) =>
    date.toLocaleDateString(undefined, { weekday: 'long' });

  // Change selected date
  const changeDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  // *** Filter exits by date + search term (LOCAL DATE FIX APPLIED) ***
  const filterExits = (exitsList, date, term) => {
    const filtered = exitsList.filter((exit) => {
      const exitDate = new Date(exit.exitedAt);

      // Fix: Compare LOCAL year, month and date (avoid UTC shift issues)
      const sameDay =
        exitDate.getFullYear() === date.getFullYear() &&
        exitDate.getMonth() === date.getMonth() &&
        exitDate.getDate() === date.getDate();

      const searchMatch =
        (exit.productName || '').toLowerCase().includes(term.toLowerCase()) ||
        (exit.brandName || '').toLowerCase().includes(term.toLowerCase()) ||
        (exit.supplierName || '').toLowerCase().includes(term.toLowerCase()) ||
        (exit.notes || '').toLowerCase().includes(term.toLowerCase()) ||
        (exit.clientName || '').toLowerCase().includes(term.toLowerCase());

      return sameDay && searchMatch;
    });

    setFilteredExits(filtered);
  };

  // Load exits from backend
  const loadExits = async () => {
    try {
      setLoading(true);
      // ✅ FIXED ENDPOINT
      const { data } = await api.get(`/inventory/exits?location=${locationKey}`);
      const arr = Array.isArray(data) ? data.slice().reverse() : [];
      setExits(arr);
      filterExits(arr, selectedDate, searchTerm);
    } catch (err) {
      setError(err?.response?.data?.message || t('errorLoadExits'));
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadExits();
  }, []);

  // Refilter when date or search term changes
  useEffect(() => {
    filterExits(exits, selectedDate, searchTerm);
  }, [exits, selectedDate, searchTerm]);

  // Delete exit
  const handleDelete = async (id) => {
    if (!await confirmAction(t('confirmDeleteExit'))) return;
    try {
      // ✅ FIXED ENDPOINT
      await api.delete(`/inventory/exits/${id}?location=${locationKey}`);
      await loadExits();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorDeleteExit'));
    }
  };

  // Export exit weights to Excel
  const handleExport = async (id) => {
    try {
      // ✅ FIXED ENDPOINT
      const response = await api.get(`/inventory/exits/${id}/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exit_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert(t('errorExport') || 'Error exporting exit');
    }
  };

  // NEW: Edit exit handler (safe mode)
  const handleEdit = (exit) => {
    setSelectedExit(exit);     // store record
    setShowEditModal(true);    // open modal
  };

  // Render
  return (
    <motion.div
      className="px-1 py-4 sm:px-3 sm:py-6 lg:px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Date selector */}
      <div className="flex flex-col items-center mb-6 select-none">
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeDate(-1)}
            className="p-2 rounded-md bg-black text-white hover:bg-white hover:text-black dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white transition"
          >
            <ChevronLeft size={18} />
          </button>

          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring focus:ring-blue-400"
          />

          <button
            onClick={() => changeDate(1)}
            className="p-2 rounded-md bg-black text-white hover:bg-white hover:text-black dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white transition"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <h2 className="mt-2 text-xl font-bold text-gray-900 dark:text-white capitalize">
          {getDayName(selectedDate)}
        </h2>
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-center mb-4 text-gray-900 dark:text-white">
        {t('exitsColdStorage1')}
      </h1>

      {/* Search */}
      <div className="max-w-3xl mx-auto mb-6 relative">
        <Search
          size={18}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          placeholder={t('searchPlaceholder') || 'Search by product, brand or supplier...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring focus:ring-blue-400 text-sm"
        />
      </div>

      {/* Main container */}
      <div className="max-w-5xl mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-xl p-3 sm:p-4 lg:p-6 backdrop-blur-md">
        {/* Header actions */}
        <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between">
          <div></div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowModal(true)} className={baseBtn}>
              <Plus size={16} /> {t('newExit')}
            </button>
            <button
              onClick={() => navigate('/products/inventory/coldstorage1')}
              className={baseBtn}
            >
              <Warehouse size={16} /> {t('inventory')}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-3 rounded-lg bg-red-600/90 text-white px-4 py-2">
            {error}
          </div>
        )}

        {/* Exits list */}
        {loading ? (
          <div className="text-gray-700 dark:text-gray-200">{t('loading')}</div>
        ) : filteredExits.length > 0 ? (
          <div className="space-y-2">
            {filteredExits.map((exit) => (
              <div
                key={getId(exit)}
                className="border border-gray-300 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-700 shadow-sm flex justify-between items-center hover:shadow-md transition"
              >
                {/* Exit info */}
                <div className="flex flex-col space-y-0.5 text-sm text-gray-700 dark:text-gray-200 w-full leading-tight">
                  <p className="font-semibold text-base text-gray-900 dark:text-white truncate">
                    {exit.productName || t('unknownProduct')}
                  </p>

                  <p className="text-sm">
                    <span className="font-medium">{t('brand')}:</span>{' '}
                    {exit.brandName || 'No Brand'}
                  </p>

                  <p className="text-sm">
                    <span className="font-medium">{t('supplier')}:</span>{' '}
                    {exit.supplierName || 'No Supplier'}
                  </p>

                  <p className="text-sm">
                    <span className="font-medium">{t('total')}:</span>{' '}
                    {(exit.totalWeight ?? 0).toFixed(2)} KG •{' '}
                    <span className="font-medium">{t('qty')}:</span>{' '}
                    {exit.quantityExited ?? 0}
                  </p>

                  <p className="italic text-sm"><span className="font-medium">{t('exitClient')}:</span> {exit.clientName || t('noClient')}</p>
                  {exit.notes && (
                    <p className="italic text-sm">
                      <span className="font-medium">{t('notes')}:</span> {exit.notes}
                    </p>
                  )}

                  {exit.exitedAt && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(exit.exitedAt).toLocaleString()}
                    </p>
                  )}
                  {exit.userId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">{t('createdBy')}:</span> {exit.userId}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-row gap-2 ml-4 shrink-0">
                  <button
                    onClick={() => handleEdit(exit)}
                    className={actionBtn}
                    title={t('editExit') || 'Edit'}
                  >
                    <Pencil size={17} />
                  </button>
                  <button
                    onClick={() => handleExport(getId(exit))}
                    className={actionBtn}
                    title={t('exportExit') || 'Export weights'}
                  >
                    <FileDown size={17} />
                  </button>
                  <button
                    onClick={() => handleDelete(getId(exit))}
                    className={actionBtn}
                    title={t('deleteExit') || 'Delete'}
                  >
                    <Trash size={17} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 dark:border-gray-700 p-5 rounded-md text-gray-500 text-center dark:text-gray-300">
            {t('noExitsForThisDate') || 'No exits for this date'}
          </div>
        )}
      </div>

      {/* Modal for new exit */}
      {showModal && (
        <AddExitModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onCreated={loadExits}
          locationKey={locationKey}
        />
      )}

      {/* NEW: Safe Edit Exit Modal */}
      {showEditModal && (
        <EditExitModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          record={selectedExit}
          locationKey={locationKey}
          onUpdated={loadExits}
        />
      )}
    </motion.div>
  );
};

export default ExitSColdStorage1;
